import React from 'react';

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  formData,
  accountTypes,
  isSubmitting,
  getLocationDisplay,
  getActionTypeDisplay
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-6 border-b border-gray-200">
          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-black font-semibold text-xl font-poppins">
            Review Your Request
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="flex flex-col gap-4">
            {/* Personal Information Card */}
            <div className="p-4 rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900 font-poppins">Personal Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Personal Email</p>
                  <p className="font-medium font-poppins">{formData.email}</p>
                </div>
              </div>
            </div>

            {/* Account Details Card */}
            <div className="p-4 rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900 font-poppins">Account Details</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Action Type</p>
                  <p className="font-medium font-poppins">{getActionTypeDisplay()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Account Type</p>
                  <p className="font-medium font-poppins">{accountTypes.find(type => type.id === formData.accountType)?.name || formData.accountType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Priority</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    formData.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                    formData.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {formData.priority}
                  </span>
                </div>
              </div>
            </div>

            {/* Location Details Card */}
            <div className="p-4 rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900 font-poppins">Location Details</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Location</p>
                  <p className="font-medium font-poppins">{getLocationDisplay()}</p>
                </div>
              </div>
            </div>

            {/* Message Details Card */}
            <div className="p-4 rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900 font-poppins">Message Details</h3>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Subject</p>
                  <p className="font-medium font-poppins">{formData.subject}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins">Message</p>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 whitespace-pre-wrap font-poppins leading-relaxed">
                    {formData.message}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 border border-black text-black rounded-lg font-medium font-poppins hover:bg-gray-50 transition-colors duration-200"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit Details
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg font-medium font-poppins hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-lg"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Confirm & Submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog; 